'use client'

import React, { useRef } from 'react'
import { formatNumberWithSuffix } from 'src/utils/formatNumberWithSuffix'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
// Undo + Repeat share the same arrow glyph; the undo button flips it
// horizontally via CSS (`--undo` modifier in chip-placement.scss).
import RepeatIcon from 'src/app/assets/icons/ico-redo-alt.svg'
import TrashIcon from 'src/app/assets/icons/ico-trash.svg'
// Match the AutoBet icon used by the default PlaceBetPanel UI
// (`AutoBetSwitch` / `AutoBetPanel`) so the visual language stays consistent.
import SyncIcon from 'src/app/assets/icons/ico-sync-alt.svg'

export type ChipStripChip = Readonly<{
  value: number
  bg: string
  border: string
  color: string
}>

type Props = Readonly<{
  // Runtime chip ladder (value + the disc's own colours). Owned by the
  // caller because the values are config/balance-driven.
  chips: readonly ChipStripChip[]
  // Index into `chips`; clamped here so an out-of-range index from a
  // caller whose ladder just changed length can't break the window maths.
  selectedChipIdx: number
  onSelectChip: (idx: number) => void
  onUndo: () => void
  onClear: () => void
  onDouble: () => void
  onRepeat: () => void
  // The board has stakes on it — enables Undo + Clear and swaps the
  // right-hand top slot from Repeat to ×2.
  hasChips: boolean
  // A previous round's bets exist to replay (only reachable while the
  // board is empty, hence the crossfade against ×2).
  canRepeat: boolean
  // Bets are in — the whole bar fades out (`--hidden`) but stays mounted.
  hidden: boolean
  // Timestamp that drives the one-shot ×2 punch animation.
  doublePunchAt: number
  autoBet: boolean
  // Timestamp that drives the one-shot auto-bet icon spin.
  autoSpinAt: number
  onToggleAutoBet: () => void
  // Birdie: the odds are new every round, so there is nothing to "auto" —
  // the toggle is replaced by the invisible spacer that keeps the two action
  // groups the same width (see the block comment above and the render below).
  hideAutoBet: boolean
  // The ladder is a single-choice group, which is exactly what `aria-pressed`
  // describes — a screen-reader player otherwise hears N buttons with no
  // selected state. Opt-in, so the twelve games that mount this through
  // `ChipPlacementPanel` render byte-identical markup to what they shipped.
  chipAriaPressed?: boolean
  // The panel measures the active chip inside the bar to launch the
  // flying chip from, so the caller keeps a handle on the bar element.
  barRef?: React.RefObject<HTMLDivElement>
}>

/**
 * The shared chip bar: two action buttons each side, chip strip in the
 * middle. The two left/right groups have matching widths so the chip strip
 * is visually centered regardless of which conditional buttons are
 * rendered.
 *
 * Extracted verbatim out of `ChipPlacementPanel` (markup, class names and
 * SCSS unchanged) so the Birdie board can render the same strip instead of
 * maintaining its own copy. Purely presentational: every mutation is a
 * prop, and the only local state is the swipe start-X.
 *
 * STYLESHEET: this file deliberately does not import `chip-placement.scss` —
 * the jest environments have no SCSS loader, so the import would land in the
 * module graph of every test that touches the strip. The rules arrive because
 * `src/app/[gameId]/page.tsx` imports `ChipPlacementPanel` STATICALLY on every
 * route and the panel imports the sheet. That import is load-bearing for the
 * Birdie board too, which renders this strip without ever mounting the panel:
 * turning it into a dynamic one would leave Birdie's chip bar as unstyled
 * native buttons. See `BirdieBoard/README.md`.
 */
export function ChipStrip({
  chips,
  selectedChipIdx,
  onSelectChip,
  onUndo,
  onClear,
  onDouble,
  onRepeat,
  hasChips,
  canRepeat,
  hidden,
  doublePunchAt,
  autoBet,
  autoSpinAt,
  onToggleAutoBet,
  hideAutoBet,
  chipAriaPressed,
  barRef
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  // Tracks the start X coord of a touch on the chip strip so we can detect
  // swipe direction in onTouchEnd. Mobile-only (desktop never fires touch).
  const chipSwipeStartX = useRef<number | null>(null)

  const safeChipIdx = Math.max(0, Math.min(selectedChipIdx, chips.length - 1))

  return (
    <div className={`chip-panel__bar ${hidden ? 'chip-panel__bar--hidden' : ''}`} ref={barRef}>
      {/* LEFT actions: Undo + Clear, both fade/scale in/out with chipCount.
          Buttons stay mounted (opacity + transform transitions) so the
          visibility change is animated instead of a hard cut. The slots
          still reserve their width because the hidden buttons keep their
          box in flex layout. */}
      <div className="chip-panel__bar-actions chip-panel__bar-actions--left">
        <button
          className={`chip-panel__action-btn chip-panel__action-btn--undo ${!hasChips ? 'chip-panel__action-btn--hidden' : ''}`}
          onClick={onUndo}
          disabled={!hasChips}
          title="Undo"
          aria-label="Undo last chip"
          aria-hidden={!hasChips}
          tabIndex={!hasChips ? -1 : 0}
        >
          <RepeatIcon />
        </button>

        <button
          className={`chip-panel__action-btn chip-panel__action-btn--clear ${!hasChips ? 'chip-panel__action-btn--hidden' : ''}`}
          onClick={onClear}
          disabled={!hasChips}
          title="Clear all"
          aria-label="Clear all"
          aria-hidden={!hasChips}
          tabIndex={!hasChips ? -1 : 0}
        >
          <TrashIcon />
        </button>
      </div>

      {(() => {
        // Mobile: each chip is absolutely positioned at a slot relative to
        // the active 3-chip window. `--chip-slot` = -1 means "left of the
        // window", 0 means "left chip in window", 1 means "centre chip", 2
        // means "right chip", 3+ or below -1 means "outside the window".
        // The CSS uses --chip-slot to translateX each chip into place, so
        // changing the selected chip smoothly slides every chip to its new
        // slot (instead of relying on a parent track + flex centering, which
        // had subpixel drift that misaligned the visible window).
        const last = chips.length - 1
        const windowStart = Math.max(0, Math.min(Math.max(0, last - 2), safeChipIdx - 1))
        return (
          <div
            className="chip-panel__bar-chips"
            onTouchStart={(e) => {
              chipSwipeStartX.current = e.touches[0]?.clientX ?? null
            }}
            onTouchEnd={(e) => {
              const startX = chipSwipeStartX.current
              chipSwipeStartX.current = null
              if (startX === null) return
              const endX = e.changedTouches[0]?.clientX
              if (endX === undefined) return
              const dx = endX - startX
              // Threshold avoids treating a tap as a swipe.
              if (Math.abs(dx) < 30) return
              // Compute outside any updater — the caller's `onSelectChip`
              // dispatches into the sound store, and doing that inside a
              // setState updater triggers React's "setState while rendering
              // another component" warning, since updaters can be invoked
              // synchronously during reconciliation. Read the latest index
              // off `safeChipIdx` (already clamped against the chips array)
              // so the closure-vs-prev difference doesn't matter.
              const nextIdx = dx < 0 ? safeChipIdx + 1 : safeChipIdx - 1
              const clamped = Math.max(0, Math.min(chips.length - 1, nextIdx))
              if (clamped !== safeChipIdx) {
                onSelectChip(clamped)
              }
            }}
          >
            <div className="chip-panel__chip-track">
              {chips.map((chip, idx) => {
                const slot = idx - windowStart
                const inWindow = slot >= 0 && slot <= 2
                return (
                  <button
                    key={`${chip.value}-${idx}`}
                    className={`chip-panel__chip ${safeChipIdx === idx ? 'chip-panel__chip--active' : ''} ${inWindow ? 'chip-panel__chip--in-window' : ''}`}
                    // Undefined prop => empty spread => the attribute is not
                    // in the markup at all, which is what keeps the twelve
                    // games' rendered DOM identical to the pre-extraction one.
                    {...(chipAriaPressed === true ? { 'aria-pressed': safeChipIdx === idx } : {})}
                    style={{
                      background: chip.bg,
                      borderColor: chip.border,
                      color: chip.color,
                      ['--chip-slot' as string]: slot
                    }}
                    onClick={() => {
                      onSelectChip(idx)
                    }}
                  >
                    {formatNumberWithSuffix(chip.value, t)}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* RIGHT actions: top slot crossfades between Repeat (no chips,
          lastBets exists) and ×2 (chips placed). Both buttons stay
          mounted and are absolutely-positioned inside `__action-slot`
          so they overlap and fade in place. Bottom slot is always the
          Auto-bet toggle. */}
      <div className="chip-panel__bar-actions chip-panel__bar-actions--right">
        {(() => {
          const showDouble = hasChips
          const showRepeat = !hasChips && canRepeat
          return (
            <div className="chip-panel__action-slot">
              <button
                className={`chip-panel__action-btn chip-panel__action-btn--double ${showDouble ? '' : 'chip-panel__action-btn--hidden'} ${doublePunchAt > 0 ? 'chip-panel__action-btn--punch' : ''}`}
                onClick={onDouble}
                disabled={!showDouble}
                title="Double all"
                aria-label="Double all"
                aria-hidden={!showDouble}
                tabIndex={showDouble ? 0 : -1}
              >
                <span>×2</span>
              </button>
              <button
                className={`chip-panel__action-btn ${showRepeat ? '' : 'chip-panel__action-btn--hidden'}`}
                onClick={onRepeat}
                disabled={!showRepeat}
                title="Repeat last"
                aria-label="Repeat last"
                aria-hidden={!showRepeat}
                tabIndex={showRepeat ? 0 : -1}
              >
                <RepeatIcon />
              </button>
            </div>
          )
        })()}
        {hideAutoBet ? (
          // RESERVE the slot rather than dropping it. The block comment above
          // promises the two groups have matching widths, and that only holds
          // while both carry two 44-px boxes: without this spacer the right
          // group is 44 px against the left group's 94 and the bar's
          // `justify-content: center` pushes the chip ladder 25 px right of
          // centre — measured at 1280 with a fine pointer, over the compiled
          // stylesheets, against the twelve games' own 0 px.
          // `--placeholder` is chip-placement.scss's own invisible action
          // button (visibility: hidden, pointer-events: none), which still
          // occupies its box. Desktop-only: birdie-board.scss drops it inside
          // the short-phone block, where the groups are columns and a second
          // box would only cost 6 px of bar height.
          <button
            aria-hidden="true"
            className="chip-panel__action-btn chip-panel__action-btn--placeholder"
            disabled
            tabIndex={-1}
          />
        ) : (
          <button
            className={`chip-panel__action-btn chip-panel__action-btn--auto ${autoBet ? 'chip-panel__action-btn--on' : ''} ${autoSpinAt > 0 ? 'chip-panel__action-btn--spinning' : ''}`}
            onClick={onToggleAutoBet}
            title="Auto"
            aria-label="Toggle auto"
          >
            <SyncIcon />
          </button>
        )}
      </div>
    </div>
  )
}
